// Configurações › Status de clientes (specs/17-status-clientes.md).
import { CORES_STATUS, GRUPOS_STATUS, STATUS_CLIENTE, type CorStatus, type GrupoStatus } from '@crm/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { Carregando, Erro, Icon, Modal, Toast, useToast } from '../components/ui';
import { CORES_UI, porGrupo, StatusBadge, useStatusClientes, type StatusCliente } from '../status';

const num = (n: number) => n.toLocaleString('pt-BR');
/** “Recorrente (Ativo)”; sem repetir quando o nome é o do grupo. */
const rotulo = (s: StatusCliente) => (s.nome === GRUPOS_STATUS[s.grupo].nome ? s.nome : `${s.nome} (${GRUPOS_STATUS[s.grupo].nome})`);
const qtdClientes = (n: number) => `${num(n)} ${n === 1 ? 'cliente' : 'clientes'}`;

export function StatusClientes() {
  const { pode } = useAuth();
  const admin = pode('CONFIGURACOES_GERENCIAR');
  const qc = useQueryClient();
  const toast = useToast();
  const q = useStatusClientes();
  const [form, setForm] = useState<StatusCliente | 'novo' | null>(null);
  const [arquivar, setArquivar] = useState<StatusCliente | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  if (q.isLoading) return <Carregando />;
  if (q.error) return <Erro erro={mensagemErro(q.error)} />;
  const lista = q.data!;
  const grupos = porGrupo(lista);
  const arquivados = lista.filter((s) => !s.ativo);

  async function recarregar(msg?: string) {
    await Promise.all([qc.invalidateQueries({ queryKey: ['status-clientes'] }), qc.invalidateQueries({ queryKey: ['clientes'] })]);
    if (msg) toast.show(msg);
  }
  async function mover(itens: StatusCliente[], i: number, dir: -1 | 1) {
    const ids = itens.map((s) => s.id);
    [ids[i], ids[i + dir]] = [ids[i + dir]!, ids[i]!];
    setErro(null);
    try {
      await api('/config/status-clientes/ordem', { method: 'PUT', body: { ids } });
      await recarregar();
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }
  async function reativar(s: StatusCliente) {
    setErro(null);
    try {
      await api(`/config/status-clientes/${s.id}/reativar`, { method: 'POST', body: {} });
      await recarregar(`“${s.nome}” reativado.`);
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  return (
    <div className="row" style={{ gap: 20, alignItems: 'flex-start' }}>
      <section className="card" style={{ flex: '999 1 620px' }} aria-labelledby="h-status">
        <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
          <div className="col" style={{ gap: 2, flex: '1 1 320px' }}>
            <h2 id="h-status" style={{ fontSize: 18 }}>Status de clientes</h2>
            <span className="small muted">Crie os status com os nomes que a sua equipe usa. Cada um pertence a um dos 3 grupos do sistema, e é o grupo que define as regras (indicadores, públicos de campanha, prospecção).</span>
          </div>
          {admin && <button className="btn primary" onClick={() => setForm('novo')}><Icon name="mais" size={14} />Novo status</button>}
        </div>
        <Erro erro={erro} />

        {grupos.map((g) => (
          <div key={g.grupo} className="col" style={{ gap: 0 }}>
            <div className="status-grupo">
              <strong>{g.nome}</strong>
              <span className="small faint">{GRUPOS_STATUS[g.grupo].descricao}</span>
              <span className="num small muted" style={{ marginLeft: 'auto' }}>{qtdClientes(g.itens.reduce((a, s) => a + s.clientes, 0))}</span>
            </div>
            {g.itens.map((s, i) => (
              <div key={s.id} className="status-linha">
                <div className="row" style={{ gap: 4, flexWrap: 'nowrap' }}>
                  {admin && (
                    <>
                      <button className="btn sm icon" aria-label={`Subir ${s.nome}`} disabled={i === 0} onClick={() => void mover(g.itens, i, -1)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 15l-6-6-6 6" /></svg>
                      </button>
                      <button className="btn sm icon" aria-label={`Descer ${s.nome}`} disabled={i === g.itens.length - 1} onClick={() => void mover(g.itens, i, 1)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
                      </button>
                    </>
                  )}
                </div>
                <div><StatusBadge nome={s.nome} cor={s.cor} /></div>
                <span className="num small muted">{qtdClientes(s.clientes)}</span>
                <div className="row" style={{ gap: 6 }}>
                  {s.padraoNovo && <span className="badge blue">Padrão p/ novos</span>}
                  {s.padraoProspeccao && <span className="badge blue">Padrão da prospecção</span>}
                  {s.sistema && <span className="badge">Original</span>}
                </div>
                <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                  {admin && <button className="btn sm" onClick={() => setForm(s)}>Editar</button>}
                  {admin && <button className="btn sm" onClick={() => setArquivar(s)}>Arquivar</button>}
                </div>
              </div>
            ))}
          </div>
        ))}

        {arquivados.length > 0 && (
          <div className="col" style={{ gap: 8, paddingTop: 6 }}>
            <span className="small faint">Arquivados — não aparecem para escolher, mas continuam no histórico dos clientes.</span>
            <div className="row" style={{ gap: 8 }}>
              {arquivados.map((s) => (
                <span key={s.id} className="row" style={{ gap: 8, padding: '4px 6px 4px 10px', border: '1px dashed var(--border-2)', borderRadius: 8 }}>
                  <span className="small muted">{rotulo(s)}</span>
                  {admin && <button className="btn sm" onClick={() => void reativar(s)}>Reativar</button>}
                </span>
              ))}
            </div>
          </div>
        )}
        {!admin && <span className="small muted">Só o administrador cria e altera status.</span>}
      </section>

      <aside className="col" style={{ flex: '1 1 280px', gap: 20 }}>
        <section className="card" aria-labelledby="h-regras">
          <h2 id="h-regras" style={{ fontSize: 15 }}>O que o grupo controla</h2>
          <ul className="small muted" style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <li><strong style={{ color: 'var(--text)', fontWeight: 500 }}>Ativo</strong> conta em “Clientes ativos” no Dashboard e é o público padrão das campanhas.</li>
            <li><strong style={{ color: 'var(--text)', fontWeight: 500 }}>Inativo</strong> fica fora dos indicadores e das campanhas, a não ser que seja escolhido no filtro.</li>
            <li><strong style={{ color: 'var(--text)', fontWeight: 500 }}>Prospect</strong> é o grupo de quem ainda não comprou.</li>
            <li>Toda troca de status de um cliente fica registrada na linha do tempo da ficha.</li>
          </ul>
        </section>
      </aside>

      {form && (
        <FormStatus
          atual={form === 'novo' ? null : form}
          onClose={() => setForm(null)}
          onSalvo={async (msg) => { setForm(null); await recarregar(msg); }}
        />
      )}
      {arquivar && (
        <ArquivarStatus
          status={arquivar}
          lista={lista}
          onClose={() => setArquivar(null)}
          onFeito={async (msg) => { setArquivar(null); await recarregar(msg); }}
        />
      )}
      <Toast msg={toast.msg} onDone={toast.clear} />
    </div>
  );
}

function FormStatus({ atual, onClose, onSalvo }: { atual: StatusCliente | null; onClose: () => void; onSalvo: (msg: string) => Promise<void> }) {
  const [nome, setNome] = useState(atual?.nome ?? '');
  const [cor, setCor] = useState<CorStatus>(atual?.cor ?? 'azul');
  const [grupo, setGrupo] = useState<GrupoStatus>(atual?.grupo ?? 'ATIVO');
  const [padraoNovo, setPadraoNovo] = useState(atual?.padraoNovo ?? false);
  const [padraoProspeccao, setPadraoProspeccao] = useState(atual?.padraoProspeccao ?? false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const c = CORES_UI[cor];
  const mudouGrupo = !!atual && atual.grupo !== grupo && atual.clientes > 0;

  async function salvar() {
    if (!nome.trim()) return setErro('Informe o nome.');
    setErro(null);
    setSalvando(true);
    try {
      if (atual) {
        const body: Record<string, unknown> = { nome: nome.trim(), cor, grupo };
        // desmarcar o padrão não é permitido (sempre existe um): só envia quando marca
        if (padraoNovo && !atual.padraoNovo) body.padraoNovo = true;
        if (padraoProspeccao && !atual.padraoProspeccao) body.padraoProspeccao = true;
        await api(`/config/status-clientes/${atual.id}`, { method: 'PATCH', body });
        await onSalvo('Status salvo.');
      } else {
        await api('/config/status-clientes', { body: { nome: nome.trim(), cor, grupo, padraoNovo, padraoProspeccao } });
        await onSalvo(`Status “${nome.trim()}” criado.`);
      }
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : mensagemErro(e));
      setSalvando(false);
    }
  }

  return (
    <Modal titulo={atual ? 'Editar status' : 'Novo status'} onClose={onClose}>
      <div className="modal-head">
        <h2>{atual ? 'Editar status' : 'Novo status'}</h2>
        <span style={{ marginLeft: 'auto' }}><StatusBadge nome={nome.trim() || 'Prévia'} cor={cor} /></span>
      </div>
      <div className="modal-body col" style={{ gap: 16 }}>
        <Erro erro={erro} />
        <label className="field"><span>Nome *</span>
          <input className="input" maxLength={40} autoFocus value={nome} placeholder="Ex.: Em negociação" onChange={(e) => setNome(e.target.value)} />
        </label>
        <div className="field">
          <span id="l-cor">Cor</span>
          <div className="row" style={{ gap: 8 }} role="group" aria-labelledby="l-cor">
            {CORES_STATUS.map((k) => (
              <button key={k} type="button" className="swatch" style={{ background: CORES_UI[k].fg }} aria-label={CORES_UI[k].nome}
                aria-pressed={cor === k} onClick={() => setCor(k)} />
            ))}
          </div>
        </div>
        <div className="field">
          <span id="l-grupo">Grupo do sistema *</span>
          <div className="grupo-opcoes" role="group" aria-labelledby="l-grupo">
            {STATUS_CLIENTE.map((g) => (
              <button key={g} type="button" className="grupo-opcao" aria-pressed={grupo === g} onClick={() => setGrupo(g)}>
                <strong style={{ fontSize: 13.5, fontWeight: 600 }}>{GRUPOS_STATUS[g].nome}</strong>
                <span className="small faint" style={{ fontSize: 11.5, lineHeight: 1.35 }}>{GRUPOS_STATUS[g].curto}</span>
              </button>
            ))}
          </div>
        </div>
        {mudouGrupo && (
          <div className="alert warn"><Icon name="alerta" /><span>Os {num(atual!.clientes)} clientes com este status passam a contar como “{GRUPOS_STATUS[grupo].nome}” nos indicadores e nos públicos de campanha.</span></div>
        )}
        <div className="col" style={{ gap: 10 }}>
          <label className="check" style={{ alignItems: 'flex-start' }}>
            <input type="checkbox" checked={padraoNovo} disabled={!!atual?.padraoNovo} onChange={(e) => setPadraoNovo(e.target.checked)} />
            <span>Padrão para clientes novos<br /><span className="small faint">Status inicial ao cadastrar um cliente.{atual?.padraoNovo ? ' Para trocar, marque outro status como padrão.' : ''}</span></span>
          </label>
          <label className="check" style={{ alignItems: 'flex-start' }}>
            <input type="checkbox" checked={padraoProspeccao} disabled={!!atual?.padraoProspeccao} onChange={(e) => setPadraoProspeccao(e.target.checked)} />
            <span>Padrão para importados da prospecção<br /><span className="small faint">Status das empresas trazidas da busca por CNPJ.{atual?.padraoProspeccao ? ' Para trocar, marque outro status como padrão.' : ''}</span></span>
          </label>
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn" onClick={onClose}>Cancelar</button>
        <button className="btn primary" disabled={salvando} onClick={() => void salvar()}>{salvando ? 'Salvando…' : 'Salvar'}</button>
      </div>
    </Modal>
  );
}

function ArquivarStatus({ status: s, lista, onClose, onFeito }: {
  status: StatusCliente; lista: StatusCliente[]; onClose: () => void; onFeito: (msg: string) => Promise<void>;
}) {
  const outros = lista.filter((o) => o.ativo && o.id !== s.id);
  const sugestao = outros.find((o) => o.grupo === s.grupo) ?? outros[0];
  const [destino, setDestino] = useState(sugestao?.id ?? '');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const noGrupo = outros.filter((o) => o.grupo === s.grupo).length;
  const bloqueio = s.padraoNovo ? 'É o status padrão para clientes novos. Marque outro como padrão antes de arquivar.'
    : s.padraoProspeccao ? 'É o status padrão da prospecção. Marque outro como padrão antes de arquivar.'
    : noGrupo === 0 ? `Cada grupo precisa de pelo menos um status. Crie outro em “${GRUPOS_STATUS[s.grupo].nome}” antes.` : null;

  async function confirmar() {
    setErro(null);
    setSalvando(true);
    try {
      const r = await api<{ movidos: number }>(`/config/status-clientes/${s.id}/arquivar`, { body: s.clientes > 0 ? { moverPara: destino } : {} });
      await onFeito(r.movidos ? `“${s.nome}” arquivado · ${qtdClientes(r.movidos)} movidos.` : `“${s.nome}” arquivado.`);
    } catch (e) {
      setErro(mensagemErro(e));
      setSalvando(false);
    }
  }

  return (
    <Modal titulo={`Arquivar ${s.nome}`} onClose={onClose}>
      <div className="modal-head"><h2>Arquivar “{s.nome}”</h2></div>
      <div className="modal-body col" style={{ gap: 14 }}>
        <Erro erro={erro} />
        {bloqueio ? (
          <div className="alert danger"><Icon name="alerta" /><span>{bloqueio}</span></div>
        ) : s.clientes > 0 ? (
          <>
            <span>{qtdClientes(s.clientes)} {s.clientes === 1 ? 'usa' : 'usam'} este status. Escolha para onde {s.clientes === 1 ? 'ele vai' : 'eles vão'}:</span>
            <label className="field"><span>Mover para *</span>
              <select className="select" value={destino} onChange={(e) => setDestino(e.target.value)}>
                {outros.map((o) => <option key={o.id} value={o.id}>{rotulo(o)}</option>)}
              </select>
            </label>
            <div className="alert info"><Icon name="alerta" /><span>Cada cliente movido ganha um registro na linha do tempo. Nada é apagado.</span></div>
          </>
        ) : (
          <span className="muted">Nenhum cliente usa este status. Ele some das listas e pode ser reativado depois.</span>
        )}
      </div>
      <div className="modal-foot">
        <button className="btn" onClick={onClose}>Cancelar</button>
        <button className="btn danger" disabled={!!bloqueio || salvando || (s.clientes > 0 && !destino)} onClick={() => void confirmar()}>
          {s.clientes > 0 && !bloqueio ? `Mover ${num(s.clientes)} e arquivar` : 'Arquivar'}
        </button>
      </div>
    </Modal>
  );
}
